import { Button, Checkbox, Form, Input, Modal, Select } from 'antd'
import { AxiosError } from 'axios'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { League } from 'types/leagues'
import { getLeagueTypes } from 'services/league-types'
import { LeagueType } from 'types/league-types'
import { getLeagueDivisions } from 'services/league-divisions'
import { LeagueDivision } from 'types/league-divisions'
import { getLeagueFormats } from 'services/league-formats'
import { LEAGUE_FORMAT_STAGES_ID, LeagueFormat } from 'types/league-formats'

type Props = {
    item: League
    onSubmit: (item: League) => Promise<void> | void
    loading: boolean
}

const BRACKET_SIZE_OPTIONS = [
    { value: 8, label: '8 teams (1/4)' },
    { value: 16, label: '16 teams (1/8)' },
    { value: 32, label: '32 teams (1/16)' },
    { value: 64, label: '64 teams (1/32)' },
]

const isResizeForceError = (error: unknown) => {
    const data = (error as AxiosError<{ message?: string | string[]; sqlMessage?: string }>)?.response?.data
    const message = data?.message
    const text = Array.isArray(message) ? message.join(' ') : message ?? data?.sqlMessage
    return typeof text === 'string' && text.includes('BRACKET_RESIZE_REQUIRES_FORCE')
}

export const LeagueForm = memo((props: Props) => {
    const { item, onSubmit, loading } = props
    const [types, setTypes] = useState<LeagueType[]>([])
    const [divisions, setDivisions] = useState<LeagueDivision[]>([])
    const [formats, setFormats] = useState<LeagueFormat[]>([])
    const [form] = Form.useForm()
    const formatId = Form.useWatch('leagueFormatId', form)
    const isStages = formatId === LEAGUE_FORMAT_STAGES_ID

    useEffect(() => {
        getLeagueTypes().then(setTypes)
    }, [])

    useEffect(() => {
        getLeagueDivisions().then(setDivisions)
    }, [])

    useEffect(() => {
        getLeagueFormats().then(setFormats)
    }, [])

    const initialValues = useMemo(
        () => ({
            name: item.name,
            short_name: item.short_name,
            logo: item.logo,
            leagueTypeId: item.leagueTypeId,
            leagueDivisionId: item.leagueDivisionId,
            leagueFormatId: item.leagueFormatId,
            bracketSize: item.bracketSize ?? undefined,
            isFinished: item.isFinished,
        }),
        [item]
    )

    const submitValues = useCallback(
        async (values: League, forceBracketResize = false) => {
            const payload: League = {
                ...values,
                bracketSize: values.leagueFormatId === LEAGUE_FORMAT_STAGES_ID ? values.bracketSize ?? null : null,
                ...(forceBracketResize ? { forceBracketResize: true } : {}),
            }

            try {
                await onSubmit(payload)
            } catch (error) {
                if (!forceBracketResize && isResizeForceError(error)) {
                    Modal.confirm({
                        title: 'Reset bracket slots?',
                        content:
                            'Bracket size change will clear all assigned games and bye teams. Continue and rebuild an empty grid?',
                        okText: 'Reset slots',
                        okType: 'danger',
                        onOk: () => submitValues(values, true),
                    })
                    return
                }
                throw error
            }
        },
        [onSubmit]
    )

    const onFinish = useCallback(
        (values: League) => {
            void submitValues(values)
        },
        [submitValues]
    )

    return (
        <Form
            form={form}
            name='basic'
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            initialValues={initialValues}
            onFinish={onFinish}
            autoComplete='off'
            disabled={loading}
        >
            <Form.Item label='Name' name='name' rules={[{ required: true, message: 'Please input league name!' }]}>
                <Input />
            </Form.Item>
            <Form.Item
                label='Short name'
                name='short_name'
                rules={[{ required: true, message: 'Please input short name!' }]}
            >
                <Input />
            </Form.Item>
            <Form.Item label='Logo' name='logo'>
                <Input />
            </Form.Item>
            <Form.Item
                name='leagueTypeId'
                label='League type'
                rules={[{ required: true, message: 'Please select league type!' }]}
            >
                <Select>
                    {types.map(it => (
                        <Select.Option key={it.id} value={it.id}>
                            {it.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name='leagueDivisionId'
                label='League division'
                rules={[{ required: true, message: 'Please select league division!' }]}
            >
                <Select>
                    {divisions.map(it => (
                        <Select.Option key={it.id} value={it.id}>
                            {it.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            <Form.Item
                name='leagueFormatId'
                label='League format'
                rules={[{ required: true, message: 'Please select league format!' }]}
            >
                <Select
                    onChange={value => {
                        if (value !== LEAGUE_FORMAT_STAGES_ID) {
                            form.setFieldValue('bracketSize', undefined)
                        }
                    }}
                >
                    {formats.map(it => (
                        <Select.Option key={it.id} value={it.id}>
                            {it.name}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>
            {isStages ? (
                <Form.Item
                    name='bracketSize'
                    label='Bracket size'
                    rules={[{ required: true, message: 'Please select bracket size!' }]}
                >
                    <Select options={BRACKET_SIZE_OPTIONS} placeholder='Teams in bracket' />
                </Form.Item>
            ) : null}
            <Form.Item name='isFinished' label='Is finished' valuePropName='checked'>
                <Checkbox />
            </Form.Item>
            <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                <Button type='primary' htmlType='submit'>
                    Submit
                </Button>
                <Button htmlType='reset'>Reset</Button>
            </Form.Item>
        </Form>
    )
})
